import React, { useState, useEffect } from "react";
import api from "../api/axios";
import "./Treasury.css";
import buttonImg from "../assets/treasury/button.png";
import btnLeftImg from "../assets/treasury/btnleft.png";

type Balance = {
  silver: number;
  gold: number;
  bank: number;
};

export default function Treasury() {
  const [rate, setRate] = useState<number | null>(null);
  const [buyAmount, setBuyAmount] = useState<number>(0);
  const [sellAmount, setSellAmount] = useState<number>(0);
  const [bal, setBal] = useState<Balance | null>(null);
  const [ecoLoading, setEcoLoading] = useState(false);

  const [depAmt, setDepAmt] = useState<number>(0);
  const [witAmt, setWitAmt] = useState<number>(0);
  const [bankLoading, setBankLoading] = useState(false);

  const [goldDeposit, setGoldDeposit] = useState<number>(0);
  const [depositInfo, setDepositInfo] = useState<{
    mintAddress: string;
    depositAccount: string;
    requiredTokens: number;
  } | null>(null);
  const [depositConfirmed, setDepositConfirmed] = useState<boolean>(false);

  // Withdrawal state
  const [goldWithdraw, setGoldWithdraw] = useState<number>(0);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState<boolean>(false);
  const [solaLoading, setSolaLoading] = useState(false);

  const [userWallet, setUserWallet] = useState<string | null>(null);
  const [view, setView] = useState<"menu" | "exchange" | "bank" | "solana">("menu");

  useEffect(() => {
    api.get<{ rate: number }>("/economy/rate")
      .then(r => setRate(r.data.rate))
      .catch(console.error);

    api.get<Balance>("/economy/balance")
      .then(r => setBal(r.data))
      .catch(console.error);

    api.get<{ wallet: string }>("/user/me")
      .then(r => setUserWallet(r.data.wallet))
      .catch(console.error);
  }, []);

  // Exchange
  const buyGold = async () => {
    if (!rate || buyAmount <= 0) return;
    setEcoLoading(true);
    try {
      const r = await api.post<{ silver: number; gold: number }>("/economy/buy", {
        amount: buyAmount,
      });
      setBal(b => b && { ...b, silver: r.data.silver, gold: r.data.gold });
      alert(`Bought ${buyAmount}g for ${rate * buyAmount}s`);
      setBuyAmount(0);
    } catch (e: any) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setEcoLoading(false);
    }
  };

  const sellGold = async () => {
    if (!rate || sellAmount <= 0) return;
    setEcoLoading(true);
    try {
      const r = await api.post<{ silver: number; gold: number }>("/economy/sell", {
        amount: sellAmount,
      });
      setBal(b => b && { ...b, silver: r.data.silver, gold: r.data.gold });
      alert(`Sold ${sellAmount}g for ${rate * sellAmount}s`);
      setSellAmount(0);
    } catch (e: any) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setEcoLoading(false);
    }
  };

  // Bank
  const depositSilver = async () => {
    if (depAmt <= 0) return;
    setBankLoading(true);
    try {
      const r = await api.post<{ silver: number; bank: number }>("/economy/deposit", {
        amount: depAmt,
      });
      setBal(b => b && { ...b, silver: r.data.silver, bank: r.data.bank });
      alert(`Deposited ${depAmt}s (minus fee)`);
      setDepAmt(0);
    } catch (e: any) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBankLoading(false);
    }
  };

  const withdrawSilver = async () => {
    if (witAmt <= 0) return;
    setBankLoading(true);
    try {
      const r = await api.post<{ silver: number; bank: number }>("/economy/withdraw", {
        amount: witAmt,
      });
      setBal(b => b && { ...b, silver: r.data.silver, bank: r.data.bank });
      alert(`Withdrew ${witAmt}s`);
      setWitAmt(0);
    } catch (e: any) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBankLoading(false);
    }
  };

  // Solana deposit
  const requestDeposit = async () => {
    if (goldDeposit <= 0) return;
    setSolaLoading(true);
    try {
      const r = await api.post<{
        mintAddress: string;
        depositAccount: string;
        requiredTokens: number;
      }>("/solana/deposit", { goldAmount: goldDeposit });
      setDepositInfo(r.data);
    } catch (e: any) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setSolaLoading(false);
    }
  };

  const confirmDeposit = async () => {
    if (!depositInfo) return;
    setSolaLoading(true);
    try {
      const r = await api.post<{ message: string }>("/solana/confirm-deposit", {
        depositAccount: depositInfo.depositAccount,
        goldAmount: goldDeposit,
      });
      alert(r.data.message);
      setDepositConfirmed(true);
    } catch (e: any) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setSolaLoading(false);
    }
  };

  // Solana withdrawal with Yes/No confirmation
  const requestWithdraw = () => {
    if (goldWithdraw <= 0) return;
    setShowWithdrawConfirm(true);
  };

  const confirmWithdraw = async () => {
    if (goldWithdraw <= 0 || !userWallet) return;
    setSolaLoading(true);
    try {
      const r = await api.post<{ message: string; gold: number; silver: number }>(
        "/solana/withdraw",
        { amount: goldWithdraw, wallet: userWallet }
      );
      alert(r.data.message);
      setBal(b => b && ({ ...b, gold: r.data.gold, silver: r.data.silver }));
      setGoldWithdraw(0);
      setShowWithdrawConfirm(false);
    } catch (e: any) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setSolaLoading(false);
    }
  };

  return (
    <div className="treasury-page">
      <div className="treasury-container">
        {view !== "menu" && (
          <button
            className="back-btn"
            onClick={() => setView("menu")}
            style={{ backgroundImage: `url(${btnLeftImg})` }}
          />
        )}

        {view === "menu" && (
          <div className="menu-buttons">
            {(["exchange", "bank", "solana"] as const).map(tab => (
              <button
                key={tab}
                className="treasury-btn menu"
                style={{ backgroundImage: `url(${buttonImg})` }}
                onClick={() => setView(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        )}

        {view === "exchange" && (
          <section className="section">
            <h3>Silver ⇄ Gold Exchange</h3>
            <p>Rate: <strong>1g = {rate ?? "–"}s</strong></p>
            {bal && <p>Your Balances: {bal.silver}s | {bal.gold}g | Bank: {bal.bank}s</p>}
            <div className="controls">
              <input
                type="number"
                value={buyAmount}
                onChange={e => setBuyAmount(+e.target.value)}
                placeholder="Gold to buy"
              />
              <button
                className="treasury-btn"
                style={{ backgroundImage: `url(${buttonImg})` }}
                onClick={buyGold}
                disabled={ecoLoading || buyAmount <= 0}
              >
                {ecoLoading ? "…" : "Buy Gold"}
              </button>
            </div>
            <div className="controls">
              <input
                type="number"
                value={sellAmount}
                onChange={e => setSellAmount(+e.target.value)}
                placeholder="Gold to sell"
              />
              <button
                className="treasury-btn"
                style={{ backgroundImage: `url(${buttonImg})` }}
                onClick={sellGold}
                disabled={ecoLoading || sellAmount <= 0}
              >
                {ecoLoading ? "…" : "Sell Gold"}
              </button>
            </div>
          </section>
        )}

        {view === "bank" && bal && (
          <section className="section">
            <h3>Bank (Silver)</h3>
            <p>
              On Hand: <strong>{bal.silver}s</strong><br/>
              In Bank: <strong>{bal.bank}s</strong>
            </p>
            <div className="controls">
              <input
                type="number"
                value={depAmt}
                onChange={e => setDepAmt(+e.target.value)}
                placeholder="Deposit silver"
              />
              <button
                className="treasury-btn"
                style={{ backgroundImage: `url(${buttonImg})` }}
                onClick={depositSilver}
                disabled={bankLoading || depAmt <= 0}
              >
                {bankLoading ? "…" : "Deposit"}
              </button>
            </div>
            <div className="controls">
              <input
                type="number"
                value={witAmt}
                onChange={e => setWitAmt(+e.target.value)}
                placeholder="Withdraw silver"
              />
              <button
                className="treasury-btn"
                style={{ backgroundImage: `url(${buttonImg})` }}
                onClick={withdrawSilver}
                disabled={bankLoading || witAmt <= 0}
              >
                {bankLoading ? "…" : "Withdraw"}
              </button>
            </div>
          </section>
        )}

        {view === "solana" && (
          <section className="section">
            <h3>Solana Deposit (→ Gold)</h3>
            <div className="controls">
              <input
                type="number"
                value={goldDeposit}
                onChange={e => setGoldDeposit(+e.target.value)}
                placeholder="Gold to deposit"
              />
              <button
                className="treasury-btn"
                style={{ backgroundImage: `url(${buttonImg})` }}
                onClick={requestDeposit}
                disabled={solaLoading || goldDeposit <= 0}
              >
                {solaLoading ? "…" : "Get Deposit Info"}
              </button>
            </div>
            {depositInfo && !depositConfirmed && (
              <div className="deposit-info">
                <p>Mint: <code>{depositInfo.mintAddress}</code></p>
                <p>Send <strong>{depositInfo.requiredTokens}</strong> tokens to:</p>
                <code className="mono">{depositInfo.depositAccount}</code>
                <button
                  className="treasury-btn controls"
                  style={{ backgroundImage: `url(${buttonImg})` }}
                  onClick={confirmDeposit}
                >
                  Confirm Deposit
                </button>
              </div>
            )}
            {depositConfirmed && (
              <div className="deposit-info">
                <p>Deposit confirmed successfully!</p>
              </div>
            )}

            <h3>Solana Withdrawal</h3>
            {!showWithdrawConfirm ? (
              <div className="controls">
                <input
                  type="number"
                  value={goldWithdraw}
                  onChange={e => setGoldWithdraw(+e.target.value)}
                  placeholder="Gold to withdraw"
                />
                <button
                  className="treasury-btn"
                  style={{ backgroundImage: `url(${buttonImg})` }}
                  onClick={requestWithdraw}
                  disabled={solaLoading || goldWithdraw <= 0}
                >
                  {solaLoading ? "…" : "Request Withdraw"}
                </button>
              </div>
            ) : (
              <div className="withdraw-confirmation">
                <p>
                  You are about to withdraw <strong>{goldWithdraw}</strong>g. Continue?
                </p>
                <p>Current rate: 10 000 $SQ per 1 gold.</p>
                <div className="controls">
                  <button
                    className="treasury-btn"
                    style={{ backgroundImage: `url(${buttonImg})` }}
                    onClick={confirmWithdraw}
                    disabled={solaLoading}
                  >
                    {solaLoading ? "…" : "Yes"}
                  </button>
                  <button
                    className="treasury-btn"
                    style={{ backgroundImage: `url(${buttonImg})` }}
                    onClick={() => setShowWithdrawConfirm(false)}
                  >
                    No
                  </button>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
